// The scoresheet: the moves of the practice laid out as a game score, and the tally above it.
// Pure, so what each cell says can be tested without a board.

import { verdictGlyph, verdictLabel } from "./moveRating.ts";
import { DEFAULT_MAX_OWN_MOVES } from "./practiceRules.ts";
import type { PracticeMove, PracticeStatus } from "../state/keys.ts";

export interface ScoresheetCell {
    ply: number;
    san: string;
    actor: "user" | "rival";
    /** The verdict code, or the status when there is no verdict yet ("pending", "unavailable"). */
    verdict: string;
    glyph: string;
    title: string;
    sharePct: number | null;
    hinted: boolean;
}

export interface ScoresheetRow {
    number: string;
    white: ScoresheetCell | null;
    black: ScoresheetCell | null;
}

function cell(move: PracticeMove): ScoresheetCell {
    if (move.actor === "rival") {
        return {
            ply: move.ply, san: move.san, actor: "rival", verdict: "", glyph: "", title: "",
            sharePct: move.sharePct ?? null, hinted: false,
        };
    }
    const rated = move.verdict.status === "rated";
    const code = rated ? (move.verdict.code ?? "") : move.verdict.status;
    return {
        ply: move.ply, san: move.san, actor: "user", verdict: code,
        glyph: rated ? verdictGlyph(move.verdict.code ?? null) : move.verdict.status === "pending" ? "…" : "",
        title: rated ? verdictLabel(move.verdict.code ?? null) : "",
        sharePct: null,
        hinted: !!move.hinted,
    };
}

/** Groups the moves played so far into numbered rows, the way a score is written. */
export function scoresheetRows(moves: (PracticeMove | undefined)[]): ScoresheetRow[] {
    const rows = new Map<string, ScoresheetRow>();
    moves.filter((m): m is PracticeMove => !!m).forEach((move) => {
        // The position before the move says whose it was and which move number it is.
        const [, turn, , , , fullmove] = move.fenBefore.split(" ");
        const number = fullmove ?? "";
        const row = rows.get(number) || { number, white: null, black: null };
        if (turn === "w") row.white = cell(move);
        else row.black = cell(move);
        rows.set(number, row);
    });
    return [...rows.values()];
}

export interface TallyView {
    moves: string;
    combo: string;
    showCombo: boolean;
    xp: string;
    accuracy: string;
}

/** The counters above the scoresheet: how far along, the combo, the XP and the accuracy. */
export function tallyView({ status, ownMoves, combo, accuracy, xp, isRetry }: {
    status: PracticeStatus;
    ownMoves: number;
    combo: number;
    accuracy: number | null;
    xp: number;
    isRetry: boolean;
}): TallyView {
    const active = ["ready", "playing", "offbook", "finished"].includes(status);
    return {
        // Past the usual length it is the repertoire that keeps it going: the count has no "of" any more
        moves: !active ? "" : ownMoves > DEFAULT_MAX_OWN_MOVES ? `Move ${ownMoves}`
            : `Move ${ownMoves} of ${DEFAULT_MAX_OWN_MOVES}`,
        combo: `Combo ×${combo}`,
        showCombo: combo >= 2,
        xp: isRetry ? "Retry" : `+${xp} XP`,
        accuracy: accuracy === null || accuracy === undefined ? "" : `${Math.round(accuracy)}% accuracy`,
    };
}
